import { forwardRef, useCallback } from 'react'

import { NumberFormatBase, NumberFormatBaseProps, NumberFormatValues, PatternFormat } from 'react-number-format'

import ArcadeInput, { ArcadeInputProps } from './ArcadeInput'

export interface FormattedInputProps extends Omit<ArcadeInputProps, 'onChange' | 'value' | 'defaultValue'> {
  applyCustomFormat?: (value: string) => string
  onChange: (value: string) => void
  showTemplateInInput?: boolean
  template: string
  type?: NumberFormatBaseProps['type']
  value: string
}

export type BaseFormattedInputProps = Omit<
  FormattedInputProps,
  'applyCustomFormat' | 'autoComplete' | 'showTemplateInInput' | 'template' | 'type'
>

// Allowed characters in template: # (digit placeholder), space, /, -, (, ), +
const ALLOWED_TEMPLATE_CHARS = /^[#\s/\-()+]+$/

const validateTemplate = (template: string): void => {
  if (!template || !ALLOWED_TEMPLATE_CHARS.test(template)) {
    throw new Error(
      `FormattedInput: Invalid template "${template}". Template must be non-empty and can only contain: # (for digits), space, /, -, (, ), +.`
    )
  }
}

export const formatTemplateValue = (value: string, template: string) => {
  if (!value) return ''

  const numericOnly = value.replace(/\D/g, '')
  if (!numericOnly) return ''

  let formatted = ''
  let numericIndex = 0

  for (let i = 0; i < template.length && numericIndex < numericOnly.length; i++) {
    if (template[i] === '#') {
      formatted += numericOnly[numericIndex]
      numericIndex++
    } else {
      if (numericIndex < numericOnly.length) {
        formatted += template[i]
      }
    }
  }

  return formatted
}

const FormattedInput = forwardRef<HTMLInputElement, FormattedInputProps>(
  (
    { value, onChange, template = '###-###', disabled, showTemplateInInput = false, applyCustomFormat, ...restProps },
    ref
  ) => {
    validateTemplate(template)
    const validateNumericOnly = useCallback((values: NumberFormatValues) => {
      const { value: inputValue } = values
      return /^\d*$/.test(inputValue)
    }, [])

    const formatValueWithoutTemplateInInput = useCallback(
      (value: string) => {
        if (value === '') {
          return ''
        }
        if (applyCustomFormat) {
          return applyCustomFormat(value)
        }
        return formatTemplateValue(value, template)
      },
      [applyCustomFormat, template]
    )

    const sharedProps = {
      customInput: ArcadeInput,
      value,
      disabled,
      inputMode: 'numeric',
      isAllowed: validateNumericOnly,
      onValueChange: (values: NumberFormatValues) => {
        onChange(values.value)
      },
      getInputRef: ref,
      ...restProps,
    } as const

    if (showTemplateInInput) {
      return <PatternFormat format={template} {...sharedProps} />
    }

    return <NumberFormatBase format={formatValueWithoutTemplateInInput} {...sharedProps} />
  }
)

FormattedInput.displayName = 'FormattedInput'

export default FormattedInput
